import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('30days');

  const bookingVolumeData = [
    { month: 'Jun', bookings: 145, revenue: 4350000 },
    { month: 'Jul', bookings: 178, revenue: 5340000 },
    { month: 'Aug', bookings: 203, revenue: 6090000 },
    { month: 'Sep', bookings: 189, revenue: 5670000 },
    { month: 'Oct', bookings: 234, revenue: 7020000 },
    { month: 'Nov', bookings: 267, revenue: 8010000 }
  ];

  const userGrowthData = [
    { month: 'Jun', buyers: 234, transporters: 156, factories: 45 },
    { month: 'Jul', buyers: 267, transporters: 178, factories: 52 },
    { month: 'Aug', buyers: 298, transporters: 203, factories: 58 },
    { month: 'Sep', buyers: 334, transporters: 221, factories: 64 },
    { month: 'Oct', buyers: 378, transporters: 245, factories: 71 },
    { month: 'Nov', buyers: 423, transporters: 278, factories: 78 }
  ];

  const revenueDistributionData = [
    { name: 'Sugar Sales', value: 45600000, color: '#2D5016' },
    { name: 'Transport Fees', value: 12800000, color: '#8B4513' },
    { name: 'Insurance', value: 3200000, color: '#FF8C00' },
    { name: 'Invoice Discount', value: 2400000, color: '#228B22' }
  ];

  const topPerformersData = [
    { name: 'Maharashtra Sugar Mills', bookings: 89, revenue: 26700000, rating: 4.8 },
    { name: 'Karnataka Sugar Factory', bookings: 76, revenue: 22800000, rating: 4.7 },
    { name: 'Tamil Nadu Co-op', bookings: 64, revenue: 19200000, rating: 4.6 },
    { name: 'Gujarat Sugar Ltd', bookings: 58, revenue: 17400000, rating: 4.5 },
    { name: 'UP Sugar Mills', bookings: 52, revenue: 15600000, rating: 4.4 }
  ];

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: '1year', label: 'Last Year' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm text-muted-foreground">
              {entry?.name}: {entry?.name?.includes('revenue') || entry?.name?.includes('Revenue') ? formatCurrency(entry?.value) : entry?.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Analytics Dashboard</h2>
              <p className="text-sm text-muted-foreground">Platform performance and insights</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={timeRangeOptions}
              value={timeRange}
              onChange={setTimeRange}
              placeholder="Select time range"
              className="w-48"
            />
            <Button variant="outline" iconName="Download" iconPosition="left">
              Export Report
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-700 dark:text-blue-400">Total Bookings</span>
              <Icon name="Package" size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">1,216</p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">+14.2% from last month</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-green-700 dark:text-green-400">Total Revenue</span>
              <Icon name="DollarSign" size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-300">{formatCurrency(36480000)}</p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">+18.5% from last month</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-purple-700 dark:text-purple-400">Active Users</span>
              <Icon name="Users" size={20} className="text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">1,089</p>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">+8.3% from last month</p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-orange-700 dark:text-orange-400">Avg. Order Value</span>
              <Icon name="TrendingUp" size={20} className="text-orange-600 dark:text-orange-400" />
            </div>
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-300">{formatCurrency(300000)}</p>
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">+3.7% from last month</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Booking Volume & Revenue Trends</h3>
            <div className="w-full h-80" aria-label="Booking Volume and Revenue Bar Chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bookingVolumeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
                  <YAxis yAxisId="left" stroke="var(--color-muted-foreground)" />
                  <YAxis yAxisId="right" orientation="right" stroke="var(--color-muted-foreground)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="bookings" fill="#2D5016" name="Bookings" />
                  <Bar yAxisId="right" dataKey="revenue" fill="#FF8C00" name="Revenue (₹)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">User Growth by Category</h3>
            <div className="w-full h-80" aria-label="User Growth Line Chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="buyers" stroke="#2D5016" strokeWidth={2} name="Buyers" />
                  <Line type="monotone" dataKey="transporters" stroke="#8B4513" strokeWidth={2} name="Transporters" />
                  <Line type="monotone" dataKey="factories" stroke="#FF8C00" strokeWidth={2} name="Factories" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Revenue Distribution</h3>
            <div className="w-full h-80" aria-label="Revenue Distribution Pie Chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={revenueDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {revenueDistributionData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Top Performing Factories</h3>
            <div className="space-y-3">
              {topPerformersData?.map((performer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{performer?.name}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Package" size={12} />
                          {performer?.bookings} bookings
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Star" size={12} className="text-yellow-500" />
                          {performer?.rating}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{formatCurrency(performer?.revenue)}</p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;